// frontend-dash/src/pages/Register.js
import React, { useState, useContext, useEffect, useCallback } from 'react';
import { trackRegistration } from '../utils/analytics';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import axios from 'axios';
import AvatarEditor from '../components/AvatarEditor';
import {
    MailIcon as Mail,
    LockIcon as Lock,
    KeyIcon as Key,
    EyeIcon as Eye,
    EyeOffIcon as EyeOff,
    CheckCircleIcon as CheckCircle2,
    AlertCircleIcon as AlertCircle,
    AddUserIcon as UserPlus,
    ArrowRightIcon as ArrowRight,
    ArrowLeftIcon as ArrowLeft,
    UploadIcon as Upload,
    CloseIcon as X,
    CelebrationIcon as Celebration,
    CameraIcon as Camera
} from '../components/Icons';

// ─── Step indicator component ───────────────────────────────────────────────

const StepIndicator = ({ currentStep, totalSteps, labels }) => (
    <div className="w-full mb-10">
        <div className="flex items-center justify-between relative">
            {/* Progress bar background */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-outlineVariant mx-8" />
            {/* Progress bar fill */}
            <div
                className="absolute top-5 left-0 h-0.5 bg-primary mx-8 transition-all duration-500 ease-out"
                style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%`, maxWidth: 'calc(100% - 4rem)' }}
            />

            {Array.from({ length: totalSteps }, (_, i) => {
                const step = i + 1;
                const isActive = step === currentStep;
                const isCompleted = step < currentStep;

                return (
                    <div key={step} className="flex flex-col items-center z-10 relative">
                        <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center text-label-large font-bold transition-all duration-300 ${
                                isCompleted
                                    ? 'bg-primary text-primary-on shadow-md scale-90'
                                    : isActive
                                        ? 'bg-primary text-primary-on shadow-lg scale-110 ring-4 ring-primary/20'
                                        : 'bg-surface-containerHighest text-surface-onVariant border-2 border-outlineVariant'
                            }`}
                        >
                            {isCompleted ? <CheckCircle2 style={{ fontSize: 20 }} /> : step}
                        </div>
                        <span className={`text-label-small mt-2 font-medium transition-colors text-center hidden sm:block ${
                            isActive ? 'text-primary' : isCompleted ? 'text-primary/70' : 'text-surface-onVariant/60'
                        }`}>
                            {labels[i]}
                        </span>
                    </div>
                );
            })}
        </div>
    </div>
);

// ─── Main Register component ────────────────────────────────────────────────

const Register = () => {
    const [searchParams] = useSearchParams();
    const urlCode = searchParams.get('code');

    const [step, setStep] = useState(1);
    const [totalSteps, setTotalSteps] = useState(4);
    const [stepLabels, setStepLabels] = useState(['Willkommen', 'Konto', 'Über dich', 'Profilbild']);
    const [suggestedEmail, setSuggestedEmail] = useState('');
    const [isEmailLoading, setIsEmailLoading] = useState(false);

    // Step 1: Code
    const [registrationCode, setRegistrationCode] = useState(urlCode?.toUpperCase() || '');
    const [codeValidated, setCodeValidated] = useState(false);
    const [codeRole, setCodeRole] = useState('');
    const [codePrefill, setCodePrefill] = useState({});
    const [codeLoading, setCodeLoading] = useState(false);
    const [codeError, setCodeError] = useState('');

    // Step 2: Account
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState({
        length: false, number: false, specialChar: false,
    });

    // Step 3: Profile
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [scoutName, setScoutName] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [seitWannDabei, setSeitWannDabei] = useState('');
    const [job, setJob] = useState('');

    // Step 4: Avatar
    const [avatarUrl, setAvatarUrl] = useState('');
    const [profileTab, setProfileTab] = useState('photo'); // 'avatar' | 'photo'
    // eslint-disable-next-line
    const [selectedFile, setSelectedFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState('');
    const [dragActive, setDragActive] = useState(false);

    // General
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    // eslint-disable-next-line
    const [slideDirection, setSlideDirection] = useState('right');

    const { registerUser, API_URL } = useContext(AuthContext);
    const { organization } = useSettings();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || '/';

    // Apply prefill data when code is validated
    useEffect(() => {
        if (codeValidated && codePrefill) {
            if (codePrefill.firstName && !firstName) setFirstName(codePrefill.firstName);
            if (codePrefill.lastName && !lastName) setLastName(codePrefill.lastName);
            if (codePrefill.scoutName && !scoutName) setScoutName(codePrefill.scoutName);
            if (codePrefill.job && !job) setJob(codePrefill.job);
            
            if (codePrefill.enableEmailRouting) {
                setTotalSteps(5);
                setStepLabels(['Willkommen', 'Konto', 'Über dich', 'Profilbild', 'Offizielle Mail']);
            } else {
                setTotalSteps(4);
                setStepLabels(['Willkommen', 'Konto', 'Über dich', 'Profilbild']);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codeValidated, codePrefill]);

    // Fetch suggested email when names change and routing is enabled
    useEffect(() => {
        const fetchSuggestedEmail = async () => {
            if (totalSteps === 5 && firstName) {
                setIsEmailLoading(true);
                try {
                    const { data } = await axios.get(`${API_URL}/auth/suggest-email`, {
                        params: { scoutName, firstName, lastName }
                    });
                    setSuggestedEmail(data.email);
                } catch (err) {
                    console.error('Error fetching suggested email:', err);
                } finally {
                    setIsEmailLoading(false);
                }
            }
        };

        const timer = setTimeout(fetchSuggestedEmail, 500);
        return () => clearTimeout(timer);
    }, [firstName, lastName, scoutName, totalSteps, API_URL]);

    const checkPasswordStrength = (pw) => {
        setPasswordStrength({
            length: pw.length >= 8,
            number: /\d/.test(pw),
            specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(pw),
        });
    };

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        checkPasswordStrength(newPassword);
    };

    // Validate registration code via API
    const validateCode = useCallback(async (codeOverride) => {
        const codeToValidate = (codeOverride || registrationCode).trim();
        if (!codeToValidate) return;

        setCodeLoading(true);
        setCodeError('');
        try {
            const { data } = await axios.get(`${API_URL}/admin/validate-code/${codeToValidate}`);
            setCodeValidated(true);
            setCodeRole(data.role);
            setCodePrefill(data.prefillData || {});
            
            if (codeOverride) {
                setRegistrationCode(codeOverride.toUpperCase());
            }
        } catch (err) {
            setCodeError(err.response?.data?.message || 'Code konnte nicht überprüft werden.');
            setCodeValidated(false);
        } finally {
            setCodeLoading(false);
        }
    }, [registrationCode, API_URL]);

    // Handle initial code from URL
    useEffect(() => {
        if (urlCode) {
            validateCode(urlCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Navigate between steps
    const goNext = () => {
        setSlideDirection('right');
        setStep(prev => Math.min(prev + 1, totalSteps));
    };

    const goBack = () => {
        setSlideDirection('left');
        setStep(prev => Math.max(prev - 1, 1));
    };

    // Can proceed to next step?
    const canProceed = () => {
        switch (step) {
            case 1: return true; // Code is optional
            case 2:
                const { length, number, specialChar } = passwordStrength;
                return email.trim() !== '' && length && number && specialChar;
            case 3: return firstName.trim() !== ''; // First name is required
            case 4: return true;
            case 5: return true;
            default: return false;
        }
    };

    // Photo handlers
    const handleDrag = (e) => {
        e.preventDefault(); e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else setDragActive(false);
    };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setDragActive(false);
        if (e.dataTransfer.files[0]) {
            setSelectedFile(e.dataTransfer.files[0]);
            setPhotoPreview(URL.createObjectURL(e.dataTransfer.files[0]));
        }
    };
    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
            setPhotoPreview(URL.createObjectURL(e.target.files[0]));
        }
    };

    // Final submit
    const handleSubmit = async () => {
        setError('');
        setIsLoading(true);

        try {
            const userData = {
                firstName,
                lastName,
                scoutName,
                birthDate: birthDate || undefined,
                job,
                standardFields: {
                    seitWannDabei
                }
            };

            await registerUser(email, password, registrationCode, userData, avatarUrl);
            trackRegistration('email');

            // After registration, user is logged in via AuthContext.registerUser
            navigate(from, { replace: true });
        } catch (error) {
            console.error('Registration failed:', error);
            if (error.response) {
                if (error.response.status === 409) {
                    navigate('/login', {
                        state: {
                            email,
                            message: 'Ein Benutzer mit dieser E-Mail-Adresse existiert bereits. Bitte melden Sie sich an.'
                        }
                    });
                } else if (error.response.status === 400) {
                    setError(error.response.data?.message || 'Ungültige Eingabedaten.');
                } else {
                    setError(error.response.data?.message || 'Registrierung fehlgeschlagen.');
                }
            } else if (error.request) {
                setError('Keine Verbindung zum Server.');
            } else {
                setError(error.message || 'Registrierung fehlgeschlagen.');
            }
            setIsLoading(false);
        }
    };

    // ─── Step Content Renderers ─────────────────────────────────────────────

    const renderStep1 = () => (
        <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-2">
                <div className="relative w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                    <div className="absolute inset-0 rounded-3xl bg-secondary opacity-10 -rotate-12 scale-110" />
                    <UserPlus className="text-secondary" style={{ fontSize: 40 }} />
                </div>
                <h2 className="text-headline-medium font-bold text-surface-on mb-2">
                    Willkommen!
                </h2>
                <p className="text-body-large text-surface-onVariant">
                    Werde Teil von {organization?.stammName || 'unserem Stamm'}
                </p>
            </div>

            {/* Code Input */}
            <div className="space-y-2">
                <label className="text-label-medium text-surface-onVariant ml-1">
                    Hast du einen Einladungscode?
                </label>
                <div className="relative group">
                    <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-surface-onVariant group-focus-within:text-primary transition-colors">
                        <Key style={{ fontSize: 20 }} />
                    </div>
                    <input
                        id="registrationCode"
                        type="text"
                        value={registrationCode}
                        onChange={(e) => {
                            setRegistrationCode(e.target.value.toUpperCase());
                            setCodeValidated(false);
                            setCodeError('');
                        }}
                        onBlur={() => registrationCode.trim() && validateCode()}
                        className="w-full h-14 pl-12 pr-28 bg-surface-containerHighest border-2 border-transparent rounded-2xl text-surface-on placeholder-surface-onVariant/50 focus:border-primary focus:bg-surface-containerHigh transition-all outline-none font-mono text-lg tracking-widest uppercase"
                        placeholder="CODE"
                        autoComplete="off"
                    />
                    <button
                        type="button"
                        onClick={validateCode}
                        disabled={!registrationCode.trim() || codeLoading}
                        className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 rounded-xl text-label-medium font-bold bg-primary text-primary-on hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                        {codeLoading ? (
                            <div className="w-4 h-4 border-2 border-primary-on/30 border-t-primary-on rounded-full animate-spin" />
                        ) : 'Prüfen'}
                    </button>
                </div>

                {/* Code validation result */}
                {codeValidated && (
                    <div className="p-4 bg-primary-container/30 border border-primary/20 rounded-2xl flex items-start gap-3 animate-fade-in">
                        <Celebration className="text-primary mt-0.5" style={{ fontSize: 20 }} />
                        <div>
                            <p className="text-label-large font-bold text-primary">
                                {codePrefill.firstName ? `Hallo ${codePrefill.firstName}!` : 'Code gültig!'}
                            </p>
                            <p className="text-body-small text-surface-onVariant">
                                Du wirst als <strong className="text-surface-on">
                                    {codeRole === 'admin' ? 'Admin' : 
                                     codeRole === 'gruppenleiter' ? 'Gruppenleiter' : 
                                     codeRole === 'eltern' ? 'Elternteil' : 
                                     codeRole === 'kind' ? 'Kind' : 'Mitglied'}
                                </strong> registriert.
                            </p>
                        </div>
                    </div>
                )}

                {codeError && (
                    <div className="p-3 bg-error-container/30 border border-error/20 rounded-xl flex items-center gap-2 animate-fade-in">
                        <AlertCircle className="text-error" style={{ fontSize: 16 }} />
                        <span className="text-label-medium text-error">{codeError}</span>
                    </div>
                )}

                <p className="text-label-small text-surface-onVariant/60 ml-1">
                    Kein Code? Kein Problem — du kannst dich auch ohne registrieren.
                </p>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-5 animate-fade-in">
            <div className="text-center mb-4">
                <h2 className="text-headline-medium font-bold text-surface-on mb-1">
                    Konto erstellen
                </h2>
                <p className="text-body-medium text-surface-onVariant">
                    Sichere dein Konto mit E-Mail und Passwort
                </p>
            </div>

            {/* Email */}
            <div className="space-y-1">
                <label htmlFor="email" className="text-label-medium text-surface-onVariant ml-1">
                    E-Mail Adresse
                </label>
                <div className="relative group">
                    <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-surface-onVariant group-focus-within:text-primary transition-colors">
                        <Mail style={{ fontSize: 20 }} />
                    </div>
                    <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full h-14 pl-12 pr-4 bg-surface-containerHighest border-2 border-transparent rounded-2xl text-surface-on placeholder-surface-onVariant/50 focus:border-primary focus:bg-surface-containerHigh transition-all outline-none"
                        placeholder="deine@email.de"
                        autoComplete="email"
                    />
                </div>
            </div>

            {/* Password */}
            <div className="space-y-1">
                <label htmlFor="password" className="text-label-medium text-surface-onVariant ml-1">
                    Passwort
                </label>
                <div className="relative group">
                    <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-surface-onVariant group-focus-within:text-primary transition-colors">
                        <Lock style={{ fontSize: 20 }} />
                    </div>
                    <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={handlePasswordChange}
                        required
                        className="w-full h-14 pl-12 pr-12 bg-surface-containerHighest border-2 border-transparent rounded-2xl text-surface-on placeholder-surface-onVariant/50 focus:border-primary focus:bg-surface-containerHigh transition-all outline-none"
                        placeholder="••••••••"
                        autoComplete="new-password"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-4 flex items-center text-surface-onVariant hover:text-surface-on transition-colors"
                    >
                        {showPassword ? <EyeOff style={{ fontSize: 20 }} /> : <Eye style={{ fontSize: 20 }} />}
                    </button>
                </div>

                {/* Password Strength */}
                <div className="pt-2 px-1 space-y-1.5">
                    {[
                        { key: 'length', text: 'Mindestens 8 Zeichen' },
                        { key: 'number', text: 'Enthält eine Zahl' },
                        { key: 'specialChar', text: 'Enthält ein Sonderzeichen' }
                    ].map((req) => (
                        <div key={req.key} className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${passwordStrength[req.key] ? 'bg-success text-success-on' : 'bg-surface-variant/40 text-surface-onVariant/30'}`}>
                                <CheckCircle2 style={{ fontSize: 12 }} />
                            </div>
                            <span className={`text-label-small transition-colors ${passwordStrength[req.key] ? 'text-success' : 'text-surface-onVariant/60'}`}>
                                {req.text}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Divider */}
            <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-outlineVariant"></div>
                </div>
                <div className="relative flex justify-center text-label-medium">
                    <span className="bg-surface-containerHigh px-4 text-surface-onVariant">ODER</span>
                </div>
            </div>

            {/* Google */}
            <button
                type="button"
                onClick={() => {
                    trackRegistration('google');
                    window.location.href = `${API_URL}/auth/google${registrationCode ? "?code=" + registrationCode : ""}`;
                }}
                className="md3-btn-outlined w-full h-14 rounded-full border-2 border-outline hover:bg-surface-containerHighest transition-all flex items-center justify-center gap-3 text-title-medium hover:border-primary/50"
            >
                <svg width="24" height="24" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 6.35l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Mit Google registrieren
            </button>
        </div>
    );

    const renderStep3 = () => (
        <div className="space-y-5 animate-fade-in">
            <div className="text-center mb-4">
                <h2 className="text-headline-medium font-bold text-surface-on mb-1">
                    Über dich
                </h2>
                <p className="text-body-medium text-surface-onVariant">
                    {codeValidated ? 'Überprüfe deine vorausgefüllten Daten' : 'Erzähl uns etwas über dich'}
                </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                    <label className="text-label-medium text-surface-onVariant ml-1">
                        Vorname <span className="text-error font-bold">*</span>
                    </label>
                    <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full h-12 px-4 bg-surface-containerHighest border-2 border-transparent rounded-2xl text-surface-on placeholder-surface-onVariant/50 focus:border-primary focus:bg-surface-containerHigh transition-all outline-none"
                        placeholder="Max"
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-label-medium text-surface-onVariant ml-1">Nachname</label>
                    <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full h-12 px-4 bg-surface-containerHighest border-2 border-transparent rounded-2xl text-surface-on placeholder-surface-onVariant/50 focus:border-primary focus:bg-surface-containerHigh transition-all outline-none"
                        placeholder="Mustermann"
                    />
                </div>
            </div>

            {codeRole !== 'eltern' && (
                <div className="space-y-1">
                    <label className="text-label-medium text-surface-onVariant ml-1">
                        Pfadiname
                        <span className="text-surface-onVariant/50 ml-1">(optional)</span>
                    </label>
                    <input
                        type="text"
                        value={scoutName}
                        onChange={(e) => setScoutName(e.target.value)}
                        className="w-full h-12 px-4 bg-surface-containerHighest border-2 border-transparent rounded-2xl text-surface-on placeholder-surface-onVariant/50 focus:border-primary focus:bg-surface-containerHigh transition-all outline-none"
                        placeholder="z.B. Akela"
                    />
                </div>
            )}

            <div className="space-y-1">
                <label className="text-label-medium text-surface-onVariant ml-1">Geburtsdatum</label>
                <input
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    className="w-full h-12 px-4 bg-surface-containerHighest border-2 border-transparent rounded-2xl text-surface-on focus:border-primary focus:bg-surface-containerHigh transition-all outline-none"
                />
            </div>

            {codeRole !== 'eltern' && (
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="text-label-medium text-surface-onVariant ml-1">Seit wann dabei?</label>
                        <input
                            type="text"
                            value={seitWannDabei}
                            onChange={(e) => setSeitWannDabei(e.target.value)}
                            className="w-full h-12 px-4 bg-surface-containerHighest border-2 border-transparent rounded-2xl text-surface-on placeholder-surface-onVariant/50 focus:border-primary focus:bg-surface-containerHigh transition-all outline-none"
                            placeholder="z.B. 2020"
                        />
                    </div>
                    {codeRole !== 'kind' && (
                        <div className="space-y-1">
                            <label className="text-label-medium text-surface-onVariant ml-1">
                                Amt / Aufgabe
                            </label>
                            <input
                                type="text"
                                value={job}
                                onChange={(e) => setJob(e.target.value)}
                                className="w-full h-12 px-4 bg-surface-containerHighest border-2 border-transparent rounded-2xl text-surface-on placeholder-surface-onVariant/50 focus:border-primary focus:bg-surface-containerHigh transition-all outline-none"
                                placeholder="z.B. Gruppenleiter, Stammesvorstand..."
                            />
                        </div>
                    )}
                </div>
            )}

            <p className="text-label-small text-surface-onVariant/60 text-center">
                Du kannst diese Daten auch später in deinem Profil ergänzen.
            </p>
        </div>
    );

    const renderStep4 = () => (
        <div className="space-y-5 animate-fade-in">
            <div className="text-center mb-4">
                <h2 className="text-headline-medium font-bold text-surface-on mb-1">
                    Profilbild
                </h2>
                <p className="text-body-medium text-surface-onVariant">
                    Wähle ein Foto oder erstelle deinen Avatar
                </p>
            </div>

            {/* Tab Switcher */}
            <div className="flex bg-surface-containerHighest rounded-2xl p-1 gap-1">
                <button
                    onClick={() => setProfileTab('photo')}
                    className={`flex-1 py-3 px-4 rounded-xl text-label-large font-bold transition-all ${
                        profileTab === 'photo'
                            ? 'bg-primary text-primary-on shadow-md'
                            : 'text-surface-onVariant hover:text-surface-on hover:bg-surface-containerHigh'
                    }`}
                >
                    <Camera style={{ fontSize: 16 }} className="inline mr-1 mb-0.5" /> Foto hochladen
                </button>
                <button
                    onClick={() => setProfileTab('avatar')}
                    className={`flex-1 py-3 px-4 rounded-xl text-label-large font-bold transition-all ${
                        profileTab === 'avatar'
                            ? 'bg-primary text-primary-on shadow-md'
                            : 'text-surface-onVariant hover:text-surface-on hover:bg-surface-containerHigh'
                    }`}
                >
                    Avatar erstellen
                </button>
            </div>

            {profileTab === 'avatar' ? (
                <div className="bg-surface-container rounded-2xl border border-outlineVariant overflow-hidden">
                    <AvatarEditor
                        onSave={(url) => {
                            setAvatarUrl(url);
                        }}
                        isSaving={false}
                    />
                </div>
            ) : (
                <div className="space-y-4">
                    <div
                        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
                            dragActive ? 'border-primary bg-primary-container/20' : 'border-outlineVariant hover:border-outline'
                        }`}
                        onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                    >
                        {photoPreview ? (
                            <div className="relative w-36 h-36 mx-auto rounded-full overflow-hidden shadow-elevation-2 border-4 border-surface">
                                <img src={photoPreview} alt="Vorschau" className="w-full h-full object-cover" />
                                <button
                                    onClick={() => { setSelectedFile(null); setPhotoPreview(''); }}
                                    className="absolute top-1 right-1 bg-surface text-error p-1.5 rounded-full shadow-elevation-1 hover:bg-surface-containerHigh"
                                >
                                    <X style={{ fontSize: 14 }} />
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center">
                                <div className="w-16 h-16 bg-primary-container rounded-full flex items-center justify-center mb-3 text-primary">
                                    <Upload style={{ fontSize: 32 }} />
                                </div>
                                <p className="text-surface-on font-bold">Foto hier reinziehen</p>
                                <p className="text-surface-onVariant text-sm mb-4">oder</p>
                                <label className="md3-btn-tonal cursor-pointer">
                                    Datei auswählen
                                    <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                                </label>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <p className="text-label-small text-surface-onVariant/60 text-center">
                Du kannst dein Profilbild jederzeit in den Einstellungen ändern.
            </p>
        </div>
    );

    const renderStep5 = () => (
        <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-6">
                <div className="relative w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                    <div className="absolute inset-0 rounded-3xl bg-primary opacity-10 rotate-12 scale-110" />
                    <Mail className="text-primary" style={{ fontSize: 40 }} />
                </div>
                <h2 className="text-headline-medium font-bold text-surface-on mb-2">
                    Deine offizielle E-Mail
                </h2>
                <p className="text-body-large text-surface-onVariant">
                    Als Teil von {organization?.stammName || 'unserem Stamm'} erhältst du eine eigene Weiterleitungsadresse.
                </p>
            </div>

            <div className="p-6 bg-surface-containerHighest rounded-[2rem] border-2 border-primary/20 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Celebration style={{ fontSize: 80 }} />
                </div>
                
                <div className="relative z-10">
                    <p className="text-label-medium text-primary font-bold mb-1 uppercase tracking-wider">Deine neue Adresse:</p>
                    {isEmailLoading ? (
                        <div className="h-10 w-48 bg-surface-variant/30 animate-pulse rounded-lg" />
                    ) : (
                        <h3 className="text-headline-small font-mono font-bold text-surface-on break-all">
                            {suggestedEmail || 'wird berechnet...'}
                        </h3>
                    )}
                    <div className="mt-6 flex items-start gap-3 p-4 bg-primary/5 rounded-2xl">
                        <CheckCircle2 className="text-primary shrink-0" style={{ fontSize: 20 }} />
                        <p className="text-body-medium text-surface-onVariant">
                            Alle E-Mails an diese Adresse werden automatisch an <strong className="text-surface-on">{email}</strong> weitergeleitet.
                        </p>
                    </div>
                </div>
            </div>

            <div className="p-4 bg-secondary-container/30 rounded-2xl flex items-start gap-3">
                <AlertCircle className="text-secondary mt-0.5" style={{ fontSize: 20 }} />
                <p className="text-label-medium text-surface-onVariant">
                    Diese Adresse wird <strong className="text-surface-on">automatisch aktiviert</strong>, sobald du deine E-Mail-Adresse im nächsten Schritt bestätigt hast.
                </p>
            </div>
        </div>
    );

    // ─── Render ─────────────────────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-surface flex flex-col overflow-x-hidden">
            <main className="flex-1 flex items-center justify-center p-4 sm:p-6 mt-16 relative">
                {/* Decorative background elements */}
                <div className="absolute top-1/4 -right-20 w-64 h-64 bg-secondary/5 rounded-full blur-3xl -z-10 animate-pulse"></div>
                <div className="absolute bottom-1/4 -left-20 w-80 h-80 bg-primary/5 rounded-full blur-3xl -z-10 animate-pulse delay-700"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-tertiary/3 rounded-full blur-3xl -z-10"></div>

                <div className={`w-full ${step === 4 ? 'max-w-4xl' : 'max-w-lg'} transition-all duration-500`}>
                    <div className="md3-card-elevated bg-surface-containerHigh p-6 sm:p-8 md:p-10 rounded-[2.5rem] shadow-elevation-3 transition-all duration-medium2">

                        {/* Step Indicator */}
                        <StepIndicator currentStep={step} totalSteps={totalSteps} labels={stepLabels} />

                        {/* Error Message */}
                        {error && (
                            <div className="mb-6 p-4 bg-error-container text-error-onContainer rounded-2xl flex items-start gap-3 animate-fade-in">
                                <AlertCircle style={{ fontSize: 20 }} className="mt-0.5 shrink-0" />
                                <p className="text-label-large">{error}</p>
                            </div>
                        )}

                        {/* Step Content */}
                        <div className="min-h-[300px]">
                            {step === 1 && renderStep1()}
                            {step === 2 && renderStep2()}
                            {step === 3 && renderStep3()}
                            {step === 4 && renderStep4()}
                            {step === 5 && renderStep5()}
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex items-center justify-between mt-8 pt-6 border-t border-outlineVariant/50">
                            {step > 1 ? (
                                <button
                                    onClick={goBack}
                                    className="flex items-center gap-2 text-label-large font-bold text-surface-onVariant hover:text-surface-on transition-colors px-4 py-2 rounded-xl hover:bg-surface-containerHighest"
                                >
                                    <ArrowLeft style={{ fontSize: 18 }} /> Zurück
                                </button>
                            ) : (
                                <div />
                            )}

                            {step < totalSteps ? (
                                <button
                                    onClick={goNext}
                                    disabled={!canProceed()}
                                    className="md3-btn-filled h-12 rounded-full px-8 text-title-medium shadow-elevation-1 hover:shadow-elevation-2 active:scale-95 transition-all flex items-center gap-2 group disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Weiter <ArrowRight style={{ fontSize: 18 }} className="group-hover:translate-x-1 transition-transform" />
                                </button>
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    disabled={isLoading}
                                    className="md3-btn-filled h-12 rounded-full px-8 text-title-medium shadow-elevation-1 hover:shadow-elevation-2 active:scale-95 transition-all flex items-center gap-2 group"
                                >
                                    {isLoading ? (
                                        <div className="w-5 h-5 border-2 border-surface/30 border-t-surface rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            Registrieren <ArrowRight style={{ fontSize: 18 }} className="group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </button>
                            )}
                        </div>

                        {/* Login Link */}
                        <div className="text-center pt-4">
                            <p className="text-body-medium text-surface-onVariant">
                                Bereits registriert?{' '}
                                <Link to="/login" className="text-primary font-bold hover:underline transition-all">
                                    Anmelden
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Register;
